import { useMutation } from '@apollo/client';
import { Box, Container, Flex, Separator } from '@energiebespaarders/symbols';
import { Book, Color, Small } from '@energiebespaarders/symbols/helpers';
import { boxShadow } from '@energiebespaarders/symbols/styles/mixins';
import styled from 'styled-components';
import HybridLink from '~/components/HybridLink';
import { useClearSession } from '~/hooks/useClearSession';
import { useMeOptional } from '~/hooks/useMe';
import { useNotificationCounter } from '~/hooks/useNotificationCounter';
import { LOGOUT_CUSTOMER } from '~/queries/customer';
import type { logoutCustomer } from '~/types/generated/logoutCustomer';
import { legacyNavMenuLinks, MainNavItem, mainNavTitles, navMenuLinks } from './constants';
import { NotificationCounter } from './Navbar';

export const StyledNavMenu = styled(Flex).attrs({ p: 2, flexWrap: 'wrap' })`
  position: fixed;
  z-index: ${x => x.theme.z.nav};
  min-height: ${x => x.theme.fixedSizes.navMenuHeight};
  top: ${x => x.theme.fixedSizes.navHeight};
  left: 0;
  right: 0;
  width: 100%;
  background: white;
  border-bottom: 3px solid ${x => x.theme.colors.greenDark};
  ${boxShadow('sm')};

  transform: translateY(0);
  transition: transform 0.2s ${x => x.theme.curves.standard};

  .headroom--unpinned & {
    transform: translateY(-100%);
  }
`;
export const StyledNavLink = styled(HybridLink)`
  position: relative;
  color: ${x => x.theme.colors.greenDarker};

  &:after {
    display: inline-block;
    width: 1rem;
    opacity: 0;
    transform: translateX(-0.25rem);
    content: '→';
    font-family: ${x => x.theme.type.fontFamilyHeadings};
    line-height: 1;
    vertical-align: middle;
    transition:
      width 0.2s ${x => x.theme.curves.standard},
      transform 0.2s ${x => x.theme.curves.standard};
  }

  &:hover {
    &:after {
      opacity: 1;
      transform: translateX(0.5rem);
    }
  }
`;

const StyledNavHeading = styled.div<{ $isClickable: boolean }>`
  letter-spacing: ${x => x.theme.type.letterSpacingHeadings};
  font-family: ${x => x.theme.type.fontFamilyHeadings};
  font-size: ${x => x.theme.type.scale[4]};
  font-weight: 500;
  margin-bottom: 0.5rem;

  ${StyledNavLink} {
    color: ${x => (x.$isClickable ? x.theme.colors.greenDark : x.theme.colors.grayBlack)};

    &:hover {
      color: ${x => x.theme.colors.green};
    }
  }
`;

export interface NavMenuProps {
  activeMenu: MainNavItem;
  handleClose: () => void;
}

const NavMenu: React.FC<NavMenuProps> = ({ activeMenu, handleClose }) => {
  const { notificationCount } = useNotificationCounter();
  const { me } = useMeOptional();
  const { clearSession } = useClearSession();
  const [logout] = useMutation<logoutCustomer>(LOGOUT_CUSTOMER, {
    onCompleted: async data => data.logoutCustomer && clearSession(),
  });

  const useLegacyAccount = me?.__typename === 'Customer' && me.isLegacyCustomer;
  const navLinks = useLegacyAccount ? legacyNavMenuLinks : navMenuLinks;

  const handleKeyDown = (e: React.KeyboardEvent, action: 'close' | 'logout') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (action === 'close') {
        handleClose();
      } else if (action === 'logout') {
        void logout();
        handleClose();
      }
    }
  };

  return (
    <StyledNavMenu
      role="menu"
      aria-label={`${mainNavTitles[activeMenu]} menu`}
      id={`nav-menu-${activeMenu}`}
      onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
        // Only close if the new focused element is not within the nav menu
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          handleClose();
        }
      }}
      tabIndex={-1}
    >
      <Container size="md" style={{ marginRight: '10%' }}>
        <Flex
          flexWrap="wrap"
          justifyContent={['space-around', 'space-between', 'flex-end']}
          mx={-1}
        >
          {navLinks[activeMenu].map((links, index) => (
            <Box key={index} width={[1, 1, 1 / navMenuLinks[activeMenu].length]} px={1}>
              {links.map(link =>
                link.isHeading ? (
                  <StyledNavHeading key={`heading-${link.to}`} $isClickable={!!link.to}>
                    {link.to ? (
                      <StyledNavLink
                        to={link.to}
                        onClick={handleClose}
                        onKeyDown={e => handleKeyDown(e, 'close')}
                        role="menuitem"
                        tabIndex={0}
                      >
                        {link.label}{' '}
                        {link.to === '/mijn-verduurzaming' && notificationCount > 0 && (
                          <NotificationCounter
                            style={{ top: 0, right: 0 }}
                            aria-label={`${notificationCount} nieuwe notificaties`}
                          >
                            {notificationCount}
                          </NotificationCounter>
                        )}
                      </StyledNavLink>
                    ) : (
                      <span role="menuitem">{link.label}</span>
                    )}
                  </StyledNavHeading>
                ) : (
                  <p key={`link-${link.to}`}>
                    <Book>
                      <StyledNavLink
                        to={link.to}
                        onClick={handleClose}
                        onKeyDown={e => handleKeyDown(e, 'close')}
                        role="menuitem"
                        tabIndex={0}
                      >
                        {link.label}
                      </StyledNavLink>
                    </Book>
                  </p>
                ),
              )}
            </Box>
          ))}
          {activeMenu === MainNavItem.Account && me?.__typename === 'Customer' && (
            <Box width={[1, 1, 1 / 2]} px={1}>
              <Separator my={2} />
              <div>
                <Color c="grayDark">
                  Ingelogd als{' '}
                  <Book>
                    {me.firstName} {me.lastName}
                  </Book>
                </Color>
              </div>
              <Small>
                <StyledNavLink
                  onClick={() => {
                    void logout();
                    handleClose();
                  }}
                  onKeyDown={e => handleKeyDown(e, 'logout')}
                  role="menuitem"
                  tabIndex={0}
                >
                  Uitloggen
                </StyledNavLink>
              </Small>
            </Box>
          )}
        </Flex>
      </Container>
    </StyledNavMenu>
  );
};

export default NavMenu;
